<template>
  <section
    ref="modalRef"
    class="edit-search__overlay"
    @click="handleOverlayClick"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="edit-search__box"
      @click.stop
    >
      <div class="edit-search__box-inner">
        <section class="edit-search__container card">
          <section class="edit-search__search">
            <SearchOrigin />
            <SearchDestination />
            <DirectionButton />
          </section>
          <div class="edit-search__calendar-desktop">
            <TravelDate />
          </div>
          <div class="edit-search__calendar-desktop">
            <ReturnDate />
          </div>
          <div class="edit-search__calendar-mobile">
            <TravelDate />
            <ReturnDate />
          </div>
          <EditPassenger />
        </section>
        <div class="edit-search__journey-wrapper">
          <SearchJourney
            class="edit-search__journey-button"
            :nextRoute="{ name: 'SearchResultPage' }"
            @close="handleOverlayClick"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent, ref, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useJourneyStore } from '@/stores/journeyStore'
  import SearchOrigin from '@/components/TravelPlanner/SearchOrigin/SearchOrigin.vue'
  import SearchDestination from '@/components/TravelPlanner/SearchDestination/SearchDestination.vue'
  import TravelDate from '@/components/TravelPlanner/Traveldate/TravelDate.vue'
  import ReturnDate from '@/components/TravelPlanner/ReturnDate/ReturnDate.vue'
  import DirectionButton from '@/components/TravelPlanner/DirectionButton.vue'
  import SearchJourney from '../SearchJourney/SearchJourney.vue'
  import EditPassenger from '@/components/TravelPlanner/PassengerPicker/EditPassenger.vue'
  import { useTabTrap } from '@/composables/useTabTrap'

  export default defineComponent({
    name: 'EditSearch',
    components: {
      SearchOrigin,
      DirectionButton,
      SearchDestination,
      TravelDate,
      ReturnDate,
      SearchJourney,
      EditPassenger,
    },
    emits: ['close'],
    setup(_, { emit }) {
      const modalRef = ref<HTMLElement | null>(null)
      useTabTrap(modalRef, false) // Disable auto-focus for EditSearch
      const router = useRouter()
      const route = useRoute()
      const journeyStore = useJourneyStore()

      const handleOverlayClick = () => {
        emit('close')
      }

      watch(
        [
          () => journeyStore.OriginStation,
          () => journeyStore.destinationStation,
          () => journeyStore.departureDate,
          () => journeyStore.returnDate,
        ],
        () => {
          const query = {
            origin: journeyStore.OriginStation?._u_i_c_station_code,
            destination: journeyStore.destinationStation?._u_i_c_station_code,
            outboundDate: journeyStore.departureDate,
            returnDate: journeyStore.returnDate || undefined,
            fromLight: route.query.fromLight ? 'true' : undefined,
          }
          Object.keys(query).forEach(
            (key) => query[key] === undefined && delete query[key]
          )
          router.replace({ query })
        },
        { deep: true }
      )

      return {
        modalRef,
        handleOverlayClick,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .edit-search {
    margin: 0 auto;
    position: relative;
    z-index: 1100;
    
    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(42, 52, 122, 0.5);
      display: flex;
      justify-content: center;
      align-items: flex-start;
      z-index: 1100;
      padding-top: 68px;
    }

    &__box {
      margin: 0 auto;
      width: 100%;
      background-color: $clr-white;
      box-shadow: $box-shadow-card;
      display: flex;
      flex-direction: column;
      align-content: center;
      align-items: center;
      justify-content: center;
    }

    &__box-inner {
      width: 100%;

      @media (min-width: $breakpoint-xl) {
        max-width: $main-width;
        margin: rem(30) 0;
      }
    }

    &__container {
      padding: rem(24) 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      & > * {
        flex: 1 1 auto;
      }

      @media (min-width: $breakpoint-xl) {
        flex-direction: row;
        align-items: center;
        align-items: stretch;
      }
    }

    &__search {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      & > * {
        flex: 1 1 auto;
      }

      @media (min-width: $breakpoint-xl) {
        flex-direction: row;
      }
    }

    &__calendar-mobile {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      flex: 1 1 auto;
      gap: 12px;
      & > * {
        flex: 1;
      }
      @media (min-width: $breakpoint-xl) {
        display: none;
      }
    }

    &__calendar-desktop {
      display: none;

      @media (min-width: $breakpoint-xl) {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        gap: 16px;
        & > * {
          flex: 1;
        }
      }
    }

    &__journey {
      &-wrapper {
        display: flex;
        justify-content: flex-end;
        padding: rem(16) 16px;
      }

      &-button {
        & > * {
          width: 100%;
        }
        /* @media (min-width: $breakpoint-xl) {
          flex: 1;
        } */
      }
    }
  }
</style>
